import { Note } from "@/components/ui/note.tsx";
import { Text } from "@/components/ui/text.tsx";
import { FilterChipMenu, type FilterChipMenuItem } from "@packages/core-client/collection/FilterChipMenu.tsx";
import { useLoadingIndicator } from "@packages/core-client/hooks/useLoadingIndicator.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useMemo } from "react";
import type { Key, Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

const NO_OPTIONS: ReadonlyArray<FilterChipMenuItem> = [];

export interface FeedbackFilterChipProps {
    dimensionLabel: string;
    options: ReadonlyArray<FilterChipMenuItem>;
    selectedId: string | null;
    isPending?: boolean;
    errorMessage?: string;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onSelect: (id: string) => void;
    onClear: () => void;
    onDismiss: () => void;
}

export function FeedbackFilterChip({ dimensionLabel, options, selectedId, isPending = false, errorMessage, isOpen, onOpenChange, onSelect, onClear, onDismiss }: FeedbackFilterChipProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("FeedbackFilterChip", { i18n: i18nextInstance });

    const isLoading = useLoadingIndicator(isPending);

    const selectedLabel = options.find(option => option.id === selectedId)?.label;
    const triggerLabel = selectedLabel ? t("chipLabel", { dimension: dimensionLabel, value: selectedLabel }) : dimensionLabel;
    const getTriggerLabel = useCallback(() => triggerLabel, [triggerLabel]);

    const selectedKeys = useMemo(() => new Set<Key>(selectedId ? [selectedId] : []), [selectedId]);

    const handleSelectionChange = useCallback(
        (selection: Selection) => {
            if (selection === "all") {
                return;
            }

            const addedKey = [...selection].find(key => key !== selectedId);

            if (addedKey === undefined) {
                onClear();
            } else {
                onSelect(String(addedKey));
            }
        },
        [selectedId, onSelect, onClear]
    );

    const showOptions = !isPending && !errorMessage;

    const popoverFooter = (
        <>
            <output className="sr-only">{isLoading && t("loading")}</output>
            {errorMessage && (
                <div className="p-1.5">
                    <Note intent="danger" role="alert">
                        {errorMessage}
                    </Note>
                </div>
            )}
            {!errorMessage && isPending && (
                <div className="p-1.5">
                    <Text aria-hidden="true" className="px-2.5 py-1.5 text-sm">
                        {t("loading")}
                    </Text>
                </div>
            )}
        </>
    );

    return (
        <FilterChipMenu
            label={dimensionLabel}
            getTriggerLabel={getTriggerLabel}
            menuAriaLabel={dimensionLabel}
            items={showOptions ? options : NO_OPTIONS}
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
            clearFilterLabel={t("clearFilter")}
            onClear={onClear}
            popoverFooter={popoverFooter}
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            dismissible
            dismissAriaLabel={t("removeFilter", { dimension: dimensionLabel })}
            onDismiss={onDismiss}
        />
    );
}
